import { message } from 'antd';

/**
 * The boolean each seeded group replaced, keyed by the group's immutable key.
 * A group an admin adds has no boolean behind it, which is why it is absent
 * here rather than defaulting to something.
 */
const LEGACY_GROUP_BOOLEANS = {
    default: (agent) => agent?.isDefaultTierEligible !== false,
    subset1: (agent) => agent?.subset1 === true,
    subset2: (agent) => agent?.subset2 === true,
    premium: (agent) => agent?.premium === true,
};

/**
 * The domain groups an agent belongs to, by name. The name is the editable half
 * of the pair, so a group renamed in Domain Mapping is renamed everywhere this
 * is shown; the booleans below are keyed by the frozen key and never by a name.
 *
 * Mirrors the rule routing applies (Helpers/DomainGroups.agentMatchesGroup):
 * membership decides, and the boolean a group replaced is read only for an
 * agent carrying no memberships at all.
 */
export const agentGroupNames = (agent, groups = []) => {
    const memberships = (agent?.domainGroups || []).map(String);

    if (memberships.length) {
        return groups
            .filter((group) => memberships.includes(String(group._id)))
            .map((group) => group.name);
    }

    return groups
        .filter((group) => group.tier === agent?.subRole)
        .filter((group) => LEGACY_GROUP_BOOLEANS[group.key]?.(agent))
        .map((group) => group.name);
};

export const Message = {
    success: (content, duration = 3) => {
        message.success({ content, duration });
    },
    error: (content, duration = 3) => {
        message.error({ content, duration });
    },
    warning: (content, duration = 3) => {
        message.warning({ content, duration });
    },
    info: (content, duration = 3) => {
        message.info({ content, duration });
    },
    loading: (content, duration = 0) => {
        message.loading({ content, duration });
    },
};

// utils/storage.js

export const Storage = {
  get(key) {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (e) {
      console.error(`Error getting ${key} from localStorage`, e);
      return null;
    }
  },

  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Error setting ${key} in localStorage`, e);
    }
  },

  remove(key) {
    localStorage.removeItem(key);
  },

  clear() {
    localStorage.clear();
  },
};

 export const formatUSPhone = (phone) => {
    if (!phone) {
      return { formatted: "—", raw: "", isValid: false, e164: "" };
    }

    // Convert to string and remove everything except digits
    const digits = String(phone).replace(/\D/g, "");

    if (digits.length === 10) {
      const area = digits.slice(0, 3);
      const prefix = digits.slice(3, 6);
      const line = digits.slice(6);
      return {
        formatted: `(${area}) ${prefix}-${line}`,
        raw: digits,
        isValid: true,
        e164: `+1${digits}`,
      };
    }

    if (digits.length === 11 && digits.startsWith("1")) {
      const area = digits.slice(1, 4);
      const prefix = digits.slice(4, 7);
      const line = digits.slice(7);
      return {
        formatted: `+1 (${area}) ${prefix}-${line}`,
        raw: digits,
        isValid: true,
        e164: `+${digits}`,
      };
    }

    // fallback — show original or cleaned version
    return {
      formatted: digits || String(phone).trim() || "—",
      raw: digits,
      isValid: false,
      e164: digits ? `+1${digits}` : "",
    };
  };


